import * as React from "react";

import "keen-slider/keen-slider.min.css";
import CircleIcon from "@mui/icons-material/Circle";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import { type KeenSliderOptions, useKeenSlider } from "keen-slider/react";

import type {
    GoalProgressJson,
    JourneyWithRelations,
    UserAtAGlance,
} from "@volley/data";

import logger from "../../log";
import { getYearInReviewYear } from "../Stats/YearInReview/YearInReviewBanner";
import { useSelectedSport, type Sport } from "../common/context/sport";
import { useCurrentUser } from "../hooks/currentUser";

import { useDuprConnect } from "./AskVolley/useDuprConnect";
import CutterChallengeCard, {
    shouldShowCutterChallenge,
} from "./CarouselCards/CutterChallengeCard";
import TakeAssessmentCard from "./CarouselCards/TakeAssessmentCard";
import YearInReviewCard from "./CarouselCards/YearInReviewCard";
import { useHomeDupr } from "./HomeDuprContext";
import { useHomePti } from "./HomePtiContext";
import StatsText from "./StatsText";
import StatusStrip from "./StatusStrip";

interface HomeCarouselProps {
    atAGlance: UserAtAGlance | null;
    goalData: GoalProgressJson | null;
    yearInReviewYears?: number[];
}

interface CalculatedState {
    showTakeAssessment: boolean;
    showYearInReview: boolean;
    cutterChallengeJourney: JourneyWithRelations | null;
    slideCount: number;
    sport: Sport;
}

export default function HomeCarousel({
    atAGlance,
    goalData,
    yearInReviewYears,
}: HomeCarouselProps): React.JSX.Element {
    const { selected: selectedSport } = useSelectedSport();
    const { features } = useCurrentUser();

    const [currentSlide, setCurrentSlide] = React.useState(0);

    const [state, setState] = React.useState<CalculatedState | null>(null);

    // DUPR (pickleball) rating shown in the StatusStrip. Shared with the
    // library's assessment entry via the home provider, which owns the sport and
    // feature gating and the "waiting, so withhold" loading state — this used to
    // be a second, independent fetch of the same profile.
    const {
        profile: duprProfile,
        loading: duprLoading,
        refetch: refetchDupr,
    } = useHomeDupr();

    // PTI (platform tennis) rating shown in the StatusStrip. Shared with the
    // library's PTI entry via the home provider, for the same reasons.
    const { rating: pti, state: ptiState } = useHomePti();

    // Re-fetch after the connect dialog closes so a freshly linked account
    // surfaces in the strip without a full reload. DuprConnect links inside its
    // own dialog and doesn't notify its host, so the close is the only signal.
    const { launchDuprConnect, dialog: duprDialog } = useDuprConnect({
        onClose: () => {
            void refetchDupr();
        },
    });

    React.useEffect(() => {
        const controller = new AbortController();

        setCurrentSlide(0);
        logger.info(
            `[Home Carousel] - Determining which home carousel items to show for ${selectedSport}`,
        );
        let number = 1;

        const showTakeAssessment =
            features.includes("JOURNEY_INSTANCES_AT_A_GLANCE") &&
            !features.includes("ASSESSMENT_DASHBOARD_V2");
        if (showTakeAssessment) {
            number += 1;
            logger.info(
                `[Home Carousel] - Will show start journey card - card count [${number}]`,
            );
        }

        const showYearInReview = (yearInReviewYears ?? []).includes(
            getYearInReviewYear(),
        );
        if (showYearInReview) {
            number += 1;
            logger.info(
                `[Home Carousel] - Will show year in review card - card count [${number}]`,
            );
        }

        const showCutterChallenge = features.includes("CUTTER_CHALLENGE");

        if (showCutterChallenge) {
            let cutterChallengeJourney: JourneyWithRelations | null = null;
            logger.info(
                `[Home Carousel] - Looking for cutter challenge journey...`,
            );
            shouldShowCutterChallenge(selectedSport, controller)
                .then((result) => {
                    cutterChallengeJourney = result;
                    if (cutterChallengeJourney) {
                        logger.info(
                            `[Home Carousel] - found cutter challenge journey - card count [${number}]`,
                        );
                        number += 1;
                    } else {
                        logger.info(
                            `[Home Carousel] - cutter challenge journey not found - card count [${number}]`,
                        );
                    }
                })
                .catch(() => {
                    logger.warn("Failed to load cutter challenge journey");
                })
                .finally(() => {
                    logger.info("[Home Carousel] - initialization complete");
                    setState({
                        cutterChallengeJourney,
                        showTakeAssessment,
                        showYearInReview,
                        slideCount: number,
                        sport: selectedSport,
                    });
                });
        } else {
            setState({
                cutterChallengeJourney: null,
                showTakeAssessment,
                showYearInReview,
                slideCount: number,
                sport: selectedSport,
            });
        }

        return () => {
            controller.abort();
        };
    }, [features, selectedSport, yearInReviewYears]);

    const options = React.useMemo<KeenSliderOptions>(
        () => ({
            slideChanged(slider) {
                setCurrentSlide(slider.track.details.rel);
            },
            slides: {
                number: state?.slideCount ?? 0,
                origin: "auto",
                perView: 1,
            },
            loop: true,
            dragSpeed: 1,
            rubberband: true,
            mode: "snap",
        }),
        [state],
    );

    const [sliderRef, slider] = useKeenSlider(options);

    if (!state) {
        return <Skeleton height={155} />;
    }

    return (
        <Stack spacing={0} mb={1}>
            <div ref={sliderRef} className="keen-slider" key={state.sport}>
                {state.showTakeAssessment && (
                    <div
                        className="keen-slider__slide"
                        key={`assessment-${state.sport}`}
                        style={{
                            display: "grid",
                            placeItems: "center",
                        }}
                    >
                        <TakeAssessmentCard />
                    </div>
                )}
                {state.cutterChallengeJourney && (
                    <div
                        className="keen-slider__slide"
                        key={`cutter-${state.sport}`}
                        style={{
                            display: "grid",
                            placeItems: "center",
                        }}
                    >
                        <CutterChallengeCard
                            journey={state.cutterChallengeJourney}
                        />
                    </div>
                )}
                {state.showYearInReview && (
                    <div
                        className="keen-slider__slide"
                        key={`review-${state.sport}`}
                        style={{
                            display: "grid",
                            placeItems: "center",
                        }}
                    >
                        <YearInReviewCard year={getYearInReviewYear()} />
                    </div>
                )}
                <div
                    className="keen-slider__slide"
                    key={`status-${state.sport}`}
                >
                    {atAGlance ? (
                        <>
                            <StatusStrip
                                atAGlance={atAGlance}
                                goalData={goalData}
                                sport={state.sport}
                                duprProfile={duprProfile}
                                duprLoading={duprLoading}
                                pti={pti}
                                ptiLoading={ptiState === "loading"}
                                onConnectDupr={launchDuprConnect}
                            />
                            <StatsText
                                globalBallsThrown={atAGlance.globalBallsThrown}
                            />
                        </>
                    ) : (
                        <Skeleton height={120} />
                    )}
                </div>
            </div>
            {state.slideCount > 1 && (
                <Stack direction="row" spacing={1} justifyContent="center">
                    {Array.from(Array(state.slideCount)).map((_, index) => (
                        <CircleIcon
                            key={index}
                            sx={{
                                color:
                                    index === currentSlide
                                        ? "primary.light"
                                        : "grey.500",
                                cursor: "pointer",
                                fontSize: "12px",
                            }}
                            onClick={() => {
                                slider.current?.moveToIdx(index);
                            }}
                        />
                    ))}
                </Stack>
            )}
            {duprDialog}
        </Stack>
    );
}
